import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, downloadHostAgent } from "@/lib/api";
import { ProjectSettings } from "@/components/workspace/ProjectSettings";
import { toast } from "sonner";
import { Download, Loader2, Terminal, User, Layers, Settings2, FolderTree } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [downloading, setDownloading] = useState(false);

  async function download() {
    setDownloading(true);
    try {
      await downloadHostAgent();
      toast.success("Host agent downloaded");
    } catch (e) {
      toast.error("Could not download the host agent");
    } finally {
      setDownloading(false);
    }
  }

  async function load() {
    try {
      const { data } = await api.get("/projects");
      setProjects(data.projects || []);
    } catch {
      /* ignore */
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="flex-1 overflow-y-auto cadence-scroll">
      <div className="max-w-3xl mx-auto px-8 py-10">
        <p className="label-caps mb-2">Account</p>
        <h1 className="font-heading font-800 text-3xl tracking-tight text-gray-900">Settings</h1>

        <section className="mt-8 bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="font-heading font-700 text-base flex items-center gap-2 mb-4"><User className="w-4 h-4" /> Profile</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="label-caps mb-1">Name</div><div className="text-gray-900">{user?.name}</div></div>
            <div><div className="label-caps mb-1">Email</div><div className="text-gray-900">{user?.email}</div></div>
          </div>
        </section>

        <section data-testid="settings-projects-section" className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="font-heading font-700 text-base flex items-center gap-2 mb-1"><Layers className="w-4 h-4" /> Project structure & preview</h2>
          <p className="text-sm text-gray-500 mb-4">
            Set each project's folder convention and its live-preview dev command. The planner organizes generated code into these folders and flags work that drifts outside them.
          </p>
          {projects.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
              No projects yet. Create one from the dashboard to configure its structure.
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((p) => (
                <div key={p.id} data-testid={`settings-project-row-${p.id}`}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-600 text-gray-900 truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-400 font-mono flex items-center gap-1.5 mt-0.5">
                      <FolderTree className="w-3 h-3" /> {p.layout?.template || "flat"}
                      {p.deploy?.preview_command && <span className="text-gray-300">·</span>}
                      {p.deploy?.preview_command && <span className="truncate">{p.deploy.preview_command}</span>}
                    </div>
                  </div>
                  <button data-testid={`settings-configure-${p.id}`} onClick={() => setEditing(p)}
                    className="ml-3 shrink-0 inline-flex items-center gap-1.5 text-sm border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 text-gray-700">
                    <Settings2 className="w-3.5 h-3.5" /> Configure
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="font-heading font-700 text-base flex items-center gap-2 mb-3"><Download className="w-4 h-4" /> Cadence Host Agent</h2>
          <p className="text-sm text-gray-500 mb-4">
            Install the host agent on your machine to work on local files with your local model auth from anywhere.
          </p>
          <button data-testid="download-host-agent" onClick={download} disabled={downloading}
            className="mb-4 bg-black text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center gap-2">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download host agent
          </button>
          <div className="bg-[#0A0A0A] text-gray-200 rounded-lg p-4 text-xs font-mono space-y-1 overflow-x-auto cadence-scroll">
            <div><span className="text-gray-500"># 1. unzip cadence-host-agent.zip, then inside that folder install deps</span></div>
            <div>pip install -r requirements.txt</div>
            <div className="pt-2"><span className="text-gray-500"># 2. pair (get code from Integrations → Hosts)</span></div>
            <div>python cadence_host.py --server wss://api.passo.link --code XXXX --root ./my-app</div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
            <Terminal className="w-3.5 h-3.5" /> The agent shells out to your local claude / codex / gemini CLI — your subscription, your machine.
          </div>
        </section>
      </div>

      {editing && (
        <ProjectSettings
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
